import { Injectable, Injector } from '@angular/core';
import * as Sentry from '@sentry/angular';

/**
 * Sentry initialization service for error tracking and performance monitoring.
 * Handles initialization, release tracking, and user context management.
 */
@Injectable({
  providedIn: 'root',
})
export class SentryService {
  private initialized = false;

  constructor(private injector: Injector) {}

  /**
   * Initialize Sentry with proper configuration.
   * Should be called during app startup via APP_INITIALIZER.
   */
  initialize(config: {
    dsn: string;
    environment: string;
    release: string;
    tracesSampleRate: number;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
  }): void {
    if (this.initialized) {
      return;
    }

    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,
      tracesSampleRate: config.tracesSampleRate,
      replaysSessionSampleRate: config.replaysSessionSampleRate,
      replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
      integrations: [
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
        Sentry.browserTracingIntegration(),
      ],
      // Attach stack traces to all messages
      attachStacktrace: true,
      // Ignore certain errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        // Network errors
        'NetworkError',
        'TimeoutError',
      ],
      denyUrls: [
        // Browser extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /moz-extension:\/\//i,
      ],
    });

    this.initialized = true;
  }

  /**
   * Set user context for better error tracking.
   * Call this after user authentication.
   */
  setUserContext(user: { id: string; email?: string; username?: string }): void {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });
  }

  /**
   * Clear user context on logout.
   */
  clearUserContext(): void {
    Sentry.setUser(null);
  }

  /**
   * Add breadcrumb for tracking user actions.
   */
  addBreadcrumb(
    message: string,
    data?: Record<string, any>,
    level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info',
  ): void {
    Sentry.captureMessage(message, level);
    Sentry.addBreadcrumb({
      message,
      level,
      data,
      timestamp: Date.now() / 1000,
    });
  }

  /**
   * Capture exception with additional context.
   */
  captureException(error: Error, context?: Record<string, any>): void {
    if (context) {
      Sentry.setContext('additional', context);
    }
    Sentry.captureException(error);
  }

  /**
   * Capture message with severity level.
   */
  captureMessage(
    message: string,
    level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info',
  ): void {
    Sentry.captureMessage(message, level);
  }

  /**
   * Start a performance span (replaces deprecated startTransaction).
   * Usage: const span = this.sentry.startSpan({name: 'api-call', op: 'http.client'}, () => {  work  })
   */
  startSpan<T>(options: { name: string; op?: string }, fn: (span: Sentry.Span) => T): T {
    return Sentry.startSpan(options, fn);
  }

  /**
   * Start a manual span (for more control over span lifecycle).
   * Usage: const span = this.sentry.startSpanManual({name: 'api-call'}, (span) => {work, return span; })
   */
  startSpanManual<T>(options: { name: string; op?: string }, fn: (span: Sentry.Span) => T): T {
    return Sentry.startSpanManual(options, fn);
  }

  /**
   * Start an inactive span (will not block on finishing).
   * Usage: this.sentry.startInactiveSpan({name: 'background-task', op: 'task'})
   */
  startInactiveSpan(options: { name: string; op?: string }): Sentry.Span {
    return Sentry.startInactiveSpan(options);
  }
}
